/**
 * useUpdateTicket Hook
 * Mutation for updating tickets with optimistic updates
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { TicketUpdate, TicketDetail } from '@/types';
import { toast } from 'sonner';
import { formatAPIError } from '@/utils/errorHandlers';

export function useUpdateTicket(ticketId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { ticketId: string; data: TicketUpdate } | TicketUpdate) => {
      // Handle both calling patterns: with ticketId in params or in hook
      const id = 'ticketId' in params ? params.ticketId : ticketId;
      const updates = 'ticketId' in params ? params.data : params;

      if (!id) {
        throw new Error('ticketId is required');
      }

      const { data } = await apiClient.patch<TicketDetail>(
        `/tickets/${id}`,
        updates
      );
      return data;
    },
    onMutate: async (params) => {
      // Extract ticketId from params or use the one provided to hook
      const id = 'ticketId' in params ? params.ticketId : ticketId;
      const updates = 'ticketId' in params ? params.data : params;

      if (!id) return {};

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.tickets.detail(id) });

      // Snapshot previous value
      const previousTicket = queryClient.getQueryData<TicketDetail>(
        queryKeys.tickets.detail(id)
      );

      // Optimistically update
      if (previousTicket) {
        queryClient.setQueryData(queryKeys.tickets.detail(id), {
          ...previousTicket,
          ...updates,
        });
      }

      return { previousTicket, ticketId: id };
    },
    onSuccess: (data, params) => {
      // Extract ticketId from params or use the one provided to hook
      const id = 'ticketId' in params ? params.ticketId : ticketId;

      if (!id) return;

      // Update cache with server data
      queryClient.setQueryData(queryKeys.tickets.detail(id), data);

      // Update in lists
      queryClient.setQueriesData(
        { queryKey: queryKeys.tickets.lists() },
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((t: any) => (t.id === id ? { ...t, ...data } : t)),
          };
        }
      );

      // Don't show toast for kanban drag-and-drop (will be handled by the component)
      if (!('ticketId' in params)) {
        toast.success('Ticket updated successfully');
      }
    },
    onError: (error: any, params, context) => {
      // Rollback on error
      if (context?.previousTicket && context?.ticketId) {
        queryClient.setQueryData(
          queryKeys.tickets.detail(context.ticketId),
          context.previousTicket
        );
      }

      // Don't show toast for kanban drag-and-drop (will be handled by the component)
      if (!('ticketId' in params)) {
        const message = formatAPIError(error, 'Failed to update ticket');
        toast.error(message);
      }
    },
  });
}
